'use client'

import { Button } from '@/components/ui/button'
import { BASE_USDC_ADDRESS, BASE_USDC_DECIMALS } from '@/constants/limitless'
import { SOLANA_USDC_MINT } from '@/hooks/kalshi/useDflowSwap'
import { useHyperliquidBalance } from '@/hooks/useHyperliquidBalance'
import { useHyperliquidPerpToSpot } from '@/hooks/useHyperliquidPerpToSpot'
import { useHyperliquidUsdhToUsdc } from '@/hooks/useHyperliquidUsdhToUsdc'
import { isHlWithdrawPending, useHyperliquidWithdraw } from '@/hooks/useHyperliquidWithdraw'
import { useMarketBalances } from '@/hooks/wallets/use-market-balances'
import { SOURCE_CHAINS } from '@/lib/api/lifi'
import { authFetch } from '@/lib/auth/auth-fetch'
import { encodeErc20Transfer } from '@/lib/onchain/erc20'
import {
	createAssociatedTokenAccountInstruction,
	createSplTransferInstruction,
	getAssociatedTokenAddress,
} from '@/lib/onchain/solana-token'
import { BSC_USDT_ADDRESS } from '@/lib/predictfun/client'
import { executeWithRelayerFallback } from '@/lib/relayer-fallback'
import { ensureGasBalance } from '@/lib/solana/ensureGasBalance'
import { sendSponsoredCalls } from '@/lib/wallet-core/sponsored'
import { invalidateMarketBalances } from '@/lib/wallets/invalidation'
import {
	buildErc20TransferCall,
	deriveDepositWalletAddress,
	ensureDepositWalletReady,
	executeDepositWalletBatch,
} from '@/lib/wallets/polymarket-deposit'
import { useTrading } from '@/providers/TradingProvider'
import { useWallet } from '@/providers/WalletContext'
import {
	getAddressFormatHint,
	isValidAddressForChain,
	truncateAddress,
} from '@/utils/address-validation'
import { OperationType, type SafeTransaction } from '@polymarket/builder-relayer-client'
import {
	PublicKey,
	type TransactionInstruction,
	TransactionMessage,
	VersionedTransaction,
} from '@solana/web3.js'
import { useQueryClient } from '@tanstack/react-query'
import { AlertCircle, CheckCircle2, ExternalLink, Loader2 } from 'lucide-react'
import { useCallback, useEffect, useMemo, useState } from 'react'
import type { Address } from 'viem'
// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

type SourceKey = 'polygon' | 'solana' | 'bsc' | 'base' | 'hyperliquid'
type StandardStatus = 'form' | 'executing' | 'success' | 'error'

// Inlined (not spread from WithdrawModal's SOURCE_OPTIONS) to avoid a circular
// import: WithdrawModal → StandardWithdrawView → WithdrawModal would read
// SOURCE_OPTIONS before its module finished evaluating.
const STANDARD_SOURCE_OPTIONS: { key: SourceKey; label: string; sublabel: string }[] = [
	{ key: 'polygon', label: 'Polymarket', sublabel: 'Polygon' },
	{ key: 'solana', label: 'Kalshi', sublabel: 'Solana' },
	{ key: 'bsc', label: 'Myriad / Predict', sublabel: 'BNB Chain' },
	{ key: 'base', label: 'Limitless', sublabel: 'Base' },
	// Hyperliquid: withdraw3 settles on Arbitrum. The full balance pool is
	// perp.withdrawable + spotUSDC + spotUSDH (USDH gets sold to USDC at
	// market on the @230 pair during the flow). $1 net HL bridge fee is
	// surfaced as a separate line. Cross-chain (HL → Polygon/Base/etc.)
	// is handled by the crosschain withdraw view, not here.
	{ key: 'hyperliquid', label: 'Hyperliquid', sublabel: 'Arbitrum' },
]

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

// V2: Safe holds pUSD on Polygon (Polymarket V2 collateral).
// Withdrawing transfers pUSD directly — recipient gets pUSD on Polygon and
// can unwrap to USDC.e themselves via the Collateral Offramp if needed.
const POLYGON_USDC_ADDRESS = '0xC011a7E12a19f7B1f670d46F03B03f3342E82DFB'
const SOLANA_VALIDATION_CHAIN_ID = 792703809

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function StandardWithdrawView({
	onBack,
	onDone,
}: {
	onBack: () => void
	onDone: () => void
}) {
	const [sourceKey, setSourceKey] = useState<SourceKey>('polygon')
	const [recipientAddress, setRecipientAddress] = useState('')
	const [amount, setAmount] = useState('')
	const [isMaxAmount, setIsMaxAmount] = useState(false)
	const [status, setStatus] = useState<StandardStatus>('form')
	const [error, setError] = useState<string | null>(null)
	const [txHash, setTxHash] = useState<string | null>(null)

	const queryClient = useQueryClient()
	const {
		eoaAddress,
		solanaAddress,
		walletClient,
		publicClient,
		evmAccount,
		solanaConnection,
		solanaSigner,
		polymarketSafe,
		polymarketActiveWallet,
	} = useWallet()
	const { funderAddress, relayClient, safeAddress: resolvedSafeAddress } = useTrading()

	// Balances
	const { balances } = useMarketBalances(['polymarket', 'kalshi', 'predictfun', 'limitless'], {
		runtime: { addressOverrides: { polymarket: funderAddress } },
	})
	const polygonRawBalance = balances.polymarket?.cash.raw ?? 0n
	const solanaRawBalance = balances.kalshi?.cash.raw ?? 0n
	const polygonBalance = balances.polymarket?.cash.amount ?? 0
	const solanaBalance = balances.kalshi?.cash.amount ?? 0
	const bscBalance = balances.predictfun?.cash.amount ?? 0
	const baseBalance = balances.limitless?.cash.amount ?? 0

	// Hyperliquid balance is split across three pools (perp, spot USDC,
	// spot USDH). Withdraw3 takes USDC FROM PERP, so the flow may need to
	// (a) sell USDH → spot USDC, then (b) transfer spot USDC → perp.
	//
	// Two ceilings shape what's actually withdrawable:
	//
	// 1. `directlyAvailable` — perp USDC + spot USDC. These can flow into
	//    withdraw3 without crossing HL's spot orderbook. After the $1
	//    validator fee, this is the floor of what we can always withdraw.
	//
	// 2. `usdhConvertible` — adds spot USDH ONLY if it's ≥$10. The
	//    USDH→USDC conversion is a spot order on @230 (USDH/USDC), which
	//    HL rejects below $10 notional (no reduce-only exemption for spot
	//    confirmed empirically). USDH below $10 is effectively stuck on
	//    HL until either market resolution credits more USDH, or the
	//    user accumulates enough to clear $10.
	//
	// Practical max withdraw = directly-available + (USDH if convertible)
	// minus the $1 validator fee.
	const { data: hlBalanceData, refetch: refetchHl } = useHyperliquidBalance()
	const hlPerp = Number.parseFloat(hlBalanceData?.perp?.withdrawable ?? '0')
	const hlSpotUsdc = Number.parseFloat(hlBalanceData?.spot?.usdc ?? '0')
	const hlSpotUsdh = Number.parseFloat(hlBalanceData?.spot?.usdh ?? '0')
	const HL_SPOT_MIN_ORDER = 10
	// USDH conversion costs ~0.5% at top-of-book (matches the 0.995 floor
	// used server-side in usdh-to-usdc/route.ts).
	const HL_USDH_TO_USDC_RATE = 0.995
	const hlUsdhConvertible = hlSpotUsdh >= HL_SPOT_MIN_ORDER ? hlSpotUsdh * HL_USDH_TO_USDC_RATE : 0
	const hlUsdhStuck = hlSpotUsdh > 0 && hlSpotUsdh < HL_SPOT_MIN_ORDER ? hlSpotUsdh : 0
	// Don't subtract $1 fee here — HL's withdraw3 deducts it from the
	// withdrawal amount on settlement. Subtracting here would leave $1
	// stranded on the user's HL account.
	const hyperliquidPracticalMax = Math.max(0, hlPerp + hlSpotUsdc + hlUsdhConvertible)
	// Legacy: this used to be raw sum of all three pools. Kept for the
	// "total holdings" display below the input — distinct from what's
	// actually withdrawable right now.
	const hyperliquidBalance = hyperliquidPracticalMax

	const hlSellUsdh = useHyperliquidUsdhToUsdc()
	const hlTransfer = useHyperliquidPerpToSpot()
	const hlWithdraw = useHyperliquidWithdraw()

	// Safe source only when the wallet switcher hasn't moved the user to the
	// deposit wallet — otherwise withdrawals must spend from the deposit wallet.
	const polymarketSafeSourceAddress = (resolvedSafeAddress ??
		(polymarketActiveWallet === 'deposit' ? undefined : polymarketSafe)) as
		| `0x${string}`
		| undefined

	const sourceBalance =
		sourceKey === 'polygon'
			? polygonBalance
			: sourceKey === 'bsc'
				? bscBalance
				: sourceKey === 'base'
					? baseBalance
					: sourceKey === 'hyperliquid'
						? hyperliquidBalance
						: solanaBalance

	// Address validation: EVM for Polygon/BSC/Base/Arbitrum (Hyperliquid),
	// Solana for Solana. We use 137 (Polygon chainId) as the canonical EVM
	// check because the underlying validator only differentiates between
	// EVM hex addresses and Solana base58 — it doesn't reject by chain.
	const addressValidationChainId = sourceKey === 'solana' ? SOLANA_VALIDATION_CHAIN_ID : 137
	const isValidAddress = recipientAddress
		? isValidAddressForChain(recipientAddress, addressValidationChainId)
		: true

	const amountNum = Number.parseFloat(amount) || 0
	const isValidAmount = amountNum > 0 && (isMaxAmount || amountNum <= sourceBalance)

	// Re-evaluates every 5s so the disabled state lifts automatically when
	// the pending-flag TTL expires (user doesn't have to manually refresh).
	// `useState(() => ...)` keeps the initial value computed lazily; the
	// interval-driven setTick forces re-renders that re-read localStorage.
	const [, setHlPendingTick] = useState(0)
	useEffect(() => {
		if (sourceKey !== 'hyperliquid' || !eoaAddress) return
		const id = setInterval(() => setHlPendingTick(t => t + 1), 5_000)
		return () => clearInterval(id)
	}, [sourceKey, eoaAddress])
	const hlWithdrawPending =
		sourceKey === 'hyperliquid' && eoaAddress ? isHlWithdrawPending(eoaAddress) : false

	const canSubmit =
		isValidAddress &&
		isValidAmount &&
		recipientAddress.length > 0 &&
		status === 'form' &&
		!hlWithdrawPending

	const handleMax = useCallback(() => {
		setAmount(sourceBalance.toFixed(2))
		setIsMaxAmount(true)
	}, [sourceBalance])

	// ------------------------------------------------------------------
	// Execute
	// ------------------------------------------------------------------
	const handleSubmit = useCallback(async () => {
		if (!canSubmit) return

		setStatus('executing')
		setError(null)
		setTxHash(null)

		// Tracks whether the HL withdraw3 (final user-signed action) was
		// submitted. Read in the catch block to decide whether to clear the
		// suppress-watchers flag — see comment near withdraw3 below.
		let hlWithdraw3Submitted = false

		try {
			// Compute raw amount (only used by polygon & solana branches — bsc and base
			// have their own raw computation using parseUnits with per-chain decimals)
			const sourceRaw =
				sourceKey === 'polygon'
					? polygonRawBalance
					: sourceKey === 'solana'
						? solanaRawBalance
						: BigInt(0) // bsc/base handled separately below

			const amountRaw = isMaxAmount ? sourceRaw : BigInt(Math.floor(amountNum * 1_000_000))

			// Only polygon & solana use the shared sourceRaw guard. BSC has 18 decimals,
			// so BSC and Base compute amount + validate inline in their branches.
			if (sourceKey === 'polygon' || sourceKey === 'solana') {
				if (amountRaw <= BigInt(0)) throw new Error('Invalid amount')
				if (amountRaw > sourceRaw) throw new Error('Insufficient balance')
			}

			if (sourceKey === 'polygon') {
				// -------------------------------------------------------
				// Polygon: pUSD transfer via Safe relayer or deposit wallet
				// -------------------------------------------------------
				if (polymarketSafeSourceAddress) {
					if (!relayClient) {
						throw new Error('Trading session not initialized. Please set up your wallet first.')
					}

					const safeTx: SafeTransaction = {
						to: POLYGON_USDC_ADDRESS,
						data: encodeErc20Transfer(recipientAddress, amountRaw),
						value: '0',
						operation: OperationType.Call,
					}

					const response = await executeWithRelayerFallback(
						relayClient,
						walletClient,
						[safeTx],
						'Withdraw pUSD (standard)'
					)
					const result = (await response.wait()) as {
						txHash?: string
						transactionHash?: string
						state?: string
					} | null

					const hash = result?.txHash || result?.transactionHash
					if (!hash) {
						throw new Error('Transaction failed on-chain. Please try again.')
					}
					setTxHash(hash)
				} else {
					if (!(walletClient && publicClient && eoaAddress)) {
						throw new Error('Wallet not connected')
					}
					const depositWalletAddress = deriveDepositWalletAddress(eoaAddress as Address)
					await ensureDepositWalletReady({ walletClient, publicClient, depositWalletAddress })

					const transferCall = buildErc20TransferCall(
						POLYGON_USDC_ADDRESS as Address,
						recipientAddress as Address,
						amountRaw
					)
					const { transactionHash } = await executeDepositWalletBatch(
						walletClient,
						[transferCall],
						depositWalletAddress
					)
					setTxHash(transactionHash)
				}
			} else if (sourceKey === 'solana') {
				// -------------------------------------------------------
				// Solana: SPL token transfer
				// -------------------------------------------------------
				if (!(solanaConnection && solanaSigner && solanaAddress)) {
					throw new Error('Solana wallet not available')
				}

				await ensureGasBalance(solanaConnection, solanaAddress, solanaSigner)

				const mintPubkey = new PublicKey(SOLANA_USDC_MINT)
				const ownerPubkey = new PublicKey(solanaAddress)
				const recipientPubkey = new PublicKey(recipientAddress)

				const sourceAta = getAssociatedTokenAddress(mintPubkey, ownerPubkey)
				const destAta = getAssociatedTokenAddress(mintPubkey, recipientPubkey)

				const instructions: TransactionInstruction[] = []

				// Create recipient's ATA if it doesn't exist
				const destAccountInfo = await solanaConnection.getAccountInfo(destAta)
				if (!destAccountInfo) {
					instructions.push(
						createAssociatedTokenAccountInstruction(
							ownerPubkey,
							destAta,
							recipientPubkey,
							mintPubkey
						)
					)
				}

				// Transfer instruction
				instructions.push(createSplTransferInstruction(sourceAta, destAta, ownerPubkey, amountRaw))

				const { blockhash } = await solanaConnection.getLatestBlockhash()
				const message = new TransactionMessage({
					payerKey: ownerPubkey,
					recentBlockhash: blockhash,
					instructions,
				}).compileToV0Message()

				const tx = new VersionedTransaction(message)
				const signedTx = await solanaSigner.addSignature(tx)

				let signature: string
				try {
					signature = await solanaConnection.sendRawTransaction(signedTx.serialize(), {
						skipPreflight: false,
						preflightCommitment: 'confirmed',
					})
				} catch (err) {
					const msg = err instanceof Error ? err.message : String(err)
					if (msg.includes('Attempt to debit an account but found no record of a prior credit')) {
						throw new Error(
							'Issues landing the transaction on Solana. The chain might be busy, please retry.'
						)
					}
					throw err
				}

				// Wait for confirmation
				for (let i = 0; i < 30; i++) {
					const txStatus = await solanaConnection.getSignatureStatus(signature)
					if (
						txStatus.value?.confirmationStatus === 'confirmed' ||
						txStatus.value?.confirmationStatus === 'finalized'
					) {
						break
					}
					if (txStatus.value?.err) {
						throw new Error(`Transaction failed: ${JSON.stringify(txStatus.value.err)}`)
					}
					await new Promise(r => setTimeout(r, 1000))
				}

				setTxHash(signature)
			}

			if (sourceKey === 'bsc') {
				// -------------------------------------------------------
				// BSC: USDT transfer via Account Kit 7702 (gasless)
				// -------------------------------------------------------
				if (!evmAccount || !eoaAddress) {
					throw new Error('Wallet not connected')
				}

				const { parseUnits } = await import('viem')

				const bscAmountRaw = isMaxAmount
					? parseUnits(bscBalance.toString(), 18)
					: parseUnits(amount, 18)

				const transferData = encodeErc20Transfer(recipientAddress, bscAmountRaw)

				const txResult = await sendSponsoredCalls({
					chain: 'bsc',
					account: evmAccount,
					calls: [{ target: BSC_USDT_ADDRESS as `0x${string}`, data: transferData }],
				})
				setTxHash(txResult)
			}

			if (sourceKey === 'base') {
				// -------------------------------------------------------
				// Base: USDC transfer via Account Kit 7702 (gasless)
				// Source of funds = user's Limitless EOA balance on Base
				// -------------------------------------------------------
				if (!evmAccount || !eoaAddress) {
					throw new Error('Wallet not connected')
				}

				const { parseUnits } = await import('viem')

				const baseAmountRaw = isMaxAmount
					? parseUnits(baseBalance.toFixed(BASE_USDC_DECIMALS), BASE_USDC_DECIMALS)
					: parseUnits(amount, BASE_USDC_DECIMALS)
				const baseBalanceRaw = parseUnits(
					baseBalance.toFixed(BASE_USDC_DECIMALS),
					BASE_USDC_DECIMALS
				)

				if (baseAmountRaw <= 0n) throw new Error('Invalid amount')
				if (baseAmountRaw > baseBalanceRaw) throw new Error('Insufficient balance')

				const transferData = encodeErc20Transfer(recipientAddress, baseAmountRaw)

				const txResult = await sendSponsoredCalls({
					chain: 'base',
					account: evmAccount,
					calls: [{ target: BASE_USDC_ADDRESS, data: transferData }],
				})
				setTxHash(txResult)
			}

			if (sourceKey === 'hyperliquid') {
				// Suppress auto-fund + auto-deposit watchers for the whole
				// multi-step flow. Without this, the auto-fund effect can
				// fire on the intermediate `spot USDC` state (post-USDH-sell,
				// pre-transfer) and convert the USDC back to USDH — the
				// subsequent usdClassTransfer would then fail with
				// "Insufficient balance for token transfer". 20-min TTL
				// covers the worst case.
				try {
					localStorage.setItem('hl-withdraw-in-progress', String(Date.now() + 20 * 60 * 1000))
				} catch {}
				// -------------------------------------------------------
				// Hyperliquid: multi-step withdraw3 → recipient on Arbitrum.
				// Pipeline (each step is a no-op when the precondition is
				// already met):
				//   0a) Sell spot USDH → USDC on @230 (agent-signed, silent)
				//   0b) usdClassTransfer spot → perp (user EIP-712)
				//   1)  withdraw3 perp → recipient (user EIP-712)
				// HL takes a $1 flat bridge fee; the recipient gets
				// (amount - $1) USDC on Arbitrum within ~3-4 min.
				// -------------------------------------------------------
				if (!eoaAddress) {
					throw new Error('Wallet not connected')
				}
				const requested = isMaxAmount ? hyperliquidBalance : amountNum
				if (requested < 2) {
					throw new Error('Minimum HL withdraw is $2 (covers $1 bridge fee)')
				}

				const fetchHlSplit = async () => {
					// All HL endpoints authenticate via Privy bearer headers.
					const r = await authFetch('/api/hyperliquid/balance', {
						headers: { 'Content-Type': 'application/json' },
					})
					if (!r.ok) throw new Error(`balance ${r.status}`)
					return (await r.json()) as {
						perp?: { withdrawable: string }
						spot?: { usdc: string; usdh: string }
						withdrawable: string
					}
				}

				let snap = await fetchHlSplit()
				let perp = Number.parseFloat(snap.perp?.withdrawable ?? snap.withdrawable)
				const spotUsdc0 = Number.parseFloat(snap.spot?.usdc ?? '0')
				const spotUsdh0 = Number.parseFloat(snap.spot?.usdh ?? '0')

				// Need a meaningful top-up only if perp is short by more than
				// the multi-step pipeline's slippage budget. A $0.01-$0.05
				// shortfall (vs requested) is what the post-withdraw clamp
				// already handles — entering top-up just to add a few cents
				// when nothing convertible exists wastes signatures or hits
				// the "no spot USDC" branch on stale dust balances.
				if (perp < requested - 0.05) {
					// Step 0a: sell spot USDH if needed for the top-up. HL
					// requires ≥ $10 notional on spot orders.
					if (spotUsdh0 > 0.01) {
						if (spotUsdh0 < 10.05) {
							throw new Error(
								`Spot USDH balance ($${spotUsdh0.toFixed(2)}) is below HL's $10 minimum order. Wait for outcome settlements to credit perp directly, or deposit more.`
							)
						}
						await hlSellUsdh.mutateAsync({ usdhAmount: spotUsdh0.toFixed(2) })
						const sellStart = Date.now()
						while (Date.now() - sellStart < 30_000) {
							const fresh = await fetchHlSplit()
							const freshUsdc = Number.parseFloat(fresh.spot?.usdc ?? '0')
							if (freshUsdc > spotUsdc0 + 9) {
								snap = fresh
								break
							}
							await new Promise(r => setTimeout(r, 2_000))
						}
					}

					// Step 0b: usdClassTransfer spot USDC → perp.
					// FLOOR to 2 decimals — toFixed(2) rounds half-up and HL
					// rejects "Insufficient balance for token transfer" by
					// fractions of a cent when actual spot USDC is e.g.
					// $19.137 (toFixed → "$19.14"). Asking for less than we
					// hold is always safe.
					const freshUsdcRaw = Number.parseFloat(snap.spot?.usdc ?? '0')
					const freshUsdc = Math.floor(freshUsdcRaw * 100) / 100
					if (freshUsdc < 0.01) {
						throw new Error(
							'No spot USDC available after sell. Refresh and retry, or contact support.'
						)
					}
					// Wait for the transfer to LAND, not for perp to reach
					// `requested`. The MAX amount displayed includes USDH which
					// gets sold at topBid*0.995 (~0.5% slippage) — so perp's
					// post-transfer value is always less than `requested` and
					// gating on `requested - 0.01` would timeout for any
					// USDH-heavy withdraw. Detect arrival via `perpBefore +
					// freshUsdc`; the withdraw3 clamp below sends whatever
					// actually landed.
					const perpBeforeTransfer = perp
					await hlTransfer.mutateAsync({ amount: freshUsdc.toFixed(2), toPerp: true })
					const transferStart = Date.now()
					const targetPerp = perpBeforeTransfer + freshUsdc - 0.05
					while (Date.now() - transferStart < 30_000) {
						const fresh = await fetchHlSplit()
						perp = Number.parseFloat(fresh.perp?.withdrawable ?? fresh.withdrawable)
						if (perp >= targetPerp) break
						await new Promise(r => setTimeout(r, 2_000))
					}
					if (perp < targetPerp) {
						throw new Error(
							`Perp top-up didn't reflect within 30s (perp = $${perp.toFixed(2)}, expected at least $${targetPerp.toFixed(2)}). Wait a moment and retry.`
						)
					}
				}

				// Step 1: withdraw3 to the user-specified Arbitrum recipient.
				// HL takes $1 net fee — display reflects that via the fee
				// row in the UI; the recipient sees `requested - 1` USDC.
				//
				// Re-read perp balance one more time. The multi-step pipeline
				// (USDH sell at topBid*0.995 + spot→perp transfer with
				// 2-decimal rounding) loses small amounts to slippage and
				// flooring — the displayed MAX is the user's GROSS balance
				// before any of these rounding losses, so withdraw3 against
				// that exact number will fail with "Insufficient balance for
				// withdrawal" by a few cents. Clamp the withdraw amount to
				// the actual on-perp number to guarantee the call succeeds.
				const finalSnap = await fetchHlSplit()
				const perpFinal = Number.parseFloat(finalSnap.perp?.withdrawable ?? finalSnap.withdrawable)
				const withdrawAmount = Math.min(requested, perpFinal).toFixed(2)
				if (Number.parseFloat(withdrawAmount) < 2) {
					throw new Error(
						`Only $${perpFinal.toFixed(2)} on perp after sell + transfer (lost to slippage). Below the $2 HL minimum withdraw — try again with more funds.`
					)
				}
				await hlWithdraw.mutateAsync({
					amount: withdrawAmount,
					destination: recipientAddress as Address,
				})
				hlWithdraw3Submitted = true
				// HL doesn't give us a settlement tx hash on /confirm — it's
				// processed off-chain by validators. Use the user's last HL
				// fills as a "submitted" marker so the success view shows
				// something explorerable; the Arbiscan link below points at
				// the recipient address page so they can watch credit land.
				setTxHash(recipientAddress)
				await refetchHl()
				// Clear the suppress-watchers flag now that withdraw3 is
				// accepted and funds are en route to an EXTERNAL recipient.
				// useHyperliquidWithdraw's onSuccess re-sets a 15-min flag,
				// which is what the stranded-recovery banner uses — but
				// that flag-purpose only applies when funds go to user's
				// OWN EOA (where the auto-deposit watcher would otherwise
				// re-grab them). External recipient = no auto-deposit risk
				// = no need to suppress watchers.
				if (eoaAddress && recipientAddress.toLowerCase() !== eoaAddress.toLowerCase()) {
					try {
						localStorage.removeItem('hl-withdraw-in-progress')
					} catch {}
				}
			}

			setStatus('success')
			invalidateMarketBalances(queryClient, [
				'polymarket',
				'kalshi',
				'predictfun',
				'limitless',
				'hyperliquid',
			])
		} catch (err) {
			const message = err instanceof Error ? err.message : 'Withdrawal failed'
			setError(message)
			setStatus('form')
			console.error('[StandardWithdraw] Error:', err)
			// Clear the suppress-flag if withdraw3 never actually went out —
			// otherwise a failed USDH sell or spot→perp transfer leaves
			// auto-fund/auto-deposit blocked for 20min even though no funds
			// were withdrawn.
			if (sourceKey === 'hyperliquid' && !hlWithdraw3Submitted) {
				try {
					localStorage.removeItem('hl-withdraw-in-progress')
				} catch {}
			}
		}
	}, [
		canSubmit,
		sourceKey,
		amount,
		amountNum,
		isMaxAmount,
		recipientAddress,
		polygonRawBalance,
		solanaRawBalance,
		relayClient,
		walletClient,
		publicClient,
		polymarketSafeSourceAddress,
		solanaConnection,
		solanaSigner,
		solanaAddress,
		evmAccount,
		eoaAddress,
		bscBalance,
		baseBalance,
		hyperliquidBalance,
		hlSellUsdh,
		hlTransfer,
		hlWithdraw,
		refetchHl,
		queryClient,
	])

	const explorerUrl = useMemo(() => {
		if (!txHash) return ''
		if (sourceKey === 'polygon') return `https://polygonscan.com/tx/${txHash}`
		if (sourceKey === 'bsc') return `https://bscscan.com/tx/${txHash}`
		if (sourceKey === 'base') return `https://basescan.org/tx/${txHash}`
		// Hyperliquid: txHash is the recipient address (validators settle
		// off-chain, no Arbitrum tx hash returned synchronously). Link to
		// the address page so the user can watch USDC credit land.
		if (sourceKey === 'hyperliquid') return `https://arbiscan.io/address/${txHash}`
		return `https://solscan.io/tx/${txHash}`
	}, [txHash, sourceKey])

	// ------------------------------------------------------------------
	// Render
	// ------------------------------------------------------------------

	// Executing spinner
	if (status === 'executing') {
		return (
			<div className="flex flex-col items-center gap-[16px] py-[32px]">
				<Loader2 className="h-12 w-12 animate-spin text-brand-blue" />
				<div className="text-center">
					<p className="font-medium text-[18px] text-white leading-[100%]">Processing Withdrawal</p>
					<p className="mt-[8px] text-[16px] text-white/70 leading-[100%]">
						{sourceKey === 'polygon'
							? 'Sending via gas-sponsored relayer...'
							: sourceKey === 'base' || sourceKey === 'bsc'
								? 'Signing gas-sponsored transfer...'
								: 'Signing and broadcasting transaction...'}
					</p>
				</div>
			</div>
		)
	}

	// Success
	if (status === 'success') {
		return (
			<div className="flex flex-col items-center gap-[16px] py-[32px]">
				<CheckCircle2 className="h-12 w-12 text-emerald-500" />
				<div className="text-center">
					<p className="font-medium text-[18px] text-white leading-[100%]">Withdrawal Complete!</p>
					<p className="mt-[8px] text-[16px] text-white/70 leading-[100%]">
						{amount} {sourceKey === 'polygon' ? 'pUSD' : sourceKey === 'bsc' ? 'USDT' : 'USDC'} sent
						to {truncateAddress(recipientAddress, 6)}
					</p>
				</div>
				{txHash && (
					<a
						href={explorerUrl}
						target="_blank"
						rel="noopener noreferrer"
						className="flex items-center gap-1 rounded-[8px] bg-white/5 px-[12px] py-[8px] text-[14px] text-blue-400 leading-[100%] hover:bg-white/10"
					>
						View Transaction <ExternalLink className="h-3 w-3" />
					</a>
				)}
				<Button
					onClick={onDone}
					className="mt-[8px] h-12 w-full rounded-[12px] bg-brand-blue font-medium text-white hover:bg-brand-blue/90"
				>
					Done
				</Button>
			</div>
		)
	}

	// Form (includes error state — user can edit and retry)
	return (
		<div className="flex flex-col gap-[24px]">
			{/* Source Toggle */}
			<div className="flex flex-col gap-[8px]">
				<p className="text-[16px] text-white/70 leading-[100%]">Withdraw from</p>
				<div className="grid grid-cols-5 rounded-[12px] border border-brand-gray bg-brand-dark p-[4px]">
					{STANDARD_SOURCE_OPTIONS.map(opt => {
						const logoSrc =
							opt.key === 'bsc'
								? '/logos/bnb.svg'
								: opt.key === 'base'
									? '/logos/base.svg'
									: opt.key === 'hyperliquid'
										? '/logos/hyperliquid.png'
										: SOURCE_CHAINS[opt.key as keyof typeof SOURCE_CHAINS].logoURI
						return (
							<button
								key={opt.key}
								type="button"
								onClick={() => {
									setSourceKey(opt.key)
									setIsMaxAmount(false)
								}}
								className={`flex flex-1 flex-col items-center justify-center gap-[6px] rounded-[8px] px-[6px] py-[10px] text-center text-[14px] leading-[100%] transition-colors ${
									sourceKey === opt.key
										? 'bg-white/10 text-white'
										: 'text-white/50 hover:text-white/70'
								}`}
							>
								<img src={logoSrc} alt={opt.label} className="h-6 w-6 shrink-0 rounded-full" />
								<div className="flex flex-col items-center gap-[2px]">
									<span className="font-medium text-[12px] leading-tight">{opt.label}</span>
									<span className="text-[10px] text-white/40 leading-tight">{opt.sublabel}</span>
								</div>
							</button>
						)
					})}
				</div>
			</div>

			{/* Amount */}
			<div className="flex flex-col gap-[12px]">
				<div className="flex flex-col gap-[12px] rounded-[16px] border border-brand-gray bg-brand-dark p-[20px]">
					<div className="flex items-center justify-between">
						<p className="text-[16px] text-white/70 leading-[100%]">Available Balance</p>
						<p className="text-[16px] text-white leading-[100%]">
							{sourceBalance.toFixed(2)}{' '}
							{sourceKey === 'bsc' ? 'USDT' : sourceKey === 'polygon' ? 'pUSD' : 'USDC'}
						</p>
					</div>
					<div className="flex items-center justify-between gap-[12px]">
						<div className="flex items-center gap-[8px] overflow-hidden">
							<input
								type="text"
								inputMode="decimal"
								value={amount}
								onChange={e => {
									const value = e.target.value
									if (value === '' || /^\d*\.?\d*$/.test(value)) {
										setAmount(value)
										setIsMaxAmount(false)
									}
								}}
								placeholder="0"
								className="field-sizing-content min-w-0 flex-1 bg-transparent text-[48px] text-white leading-[100%] [appearance:textfield] placeholder:text-white/30 focus:outline-none [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none"
							/>
							<span className="shrink-0 text-[24px] text-white/50 leading-[100%]">
								{sourceKey === 'bsc' ? 'USDT' : sourceKey === 'polygon' ? 'pUSD' : 'USDC'}
							</span>
						</div>
						<button
							type="button"
							onClick={handleMax}
							className="shrink-0 rounded-[12px] bg-white/5 px-[16px] py-[10px] text-[14px] text-white leading-[100%] transition-colors hover:bg-white/20"
						>
							MAX
						</button>
					</div>
					{!isMaxAmount && amountNum > sourceBalance && (
						<p className="text-red-500 text-xs">Insufficient balance</p>
					)}
					{sourceKey === 'hyperliquid' && hlUsdhStuck > 0 && (
						<p className="text-[11px] text-white/50 leading-snug">
							${hlUsdhStuck.toFixed(2)} USDH held in HIP-4 shares is under HL's $10 spot conversion
							minimum and can't be withdrawn yet. Wait for market resolution or trade to reach $10+
							USDH on spot.
						</p>
					)}
					{hlWithdrawPending && (
						<p className="text-[11px] text-yellow-400 leading-snug">
							A previous Hyperliquid withdrawal may still be settling. Check your HL balance — if
							the funds didn't arrive, this lifts automatically after 5 minutes.
						</p>
					)}
				</div>
			</div>

			{/* Recipient */}
			<div className="flex flex-col gap-[8px]">
				<p className="text-[16px] text-white/70 leading-[100%]">
					Recipient Address (
					{sourceKey === 'bsc'
						? 'BNB Chain'
						: sourceKey === 'polygon'
							? 'Polygon'
							: sourceKey === 'base'
								? 'Base'
								: sourceKey === 'hyperliquid'
									? 'Arbitrum'
									: 'Solana'}
					)
				</p>
				<input
					type="text"
					value={recipientAddress}
					onChange={e => setRecipientAddress(e.target.value)}
					placeholder={getAddressFormatHint(addressValidationChainId)}
					className={`w-full rounded-[8px] border bg-brand-dark px-[12px] py-[15px] text-[14px] text-white leading-[100%] placeholder:text-white/75 focus:outline-none focus:ring-1 ${
						recipientAddress && !isValidAddress
							? 'border-red-500 focus:ring-red-500'
							: 'border-brand-gray focus:ring-brand-blue'
					}`}
				/>
				{recipientAddress && !isValidAddress && (
					<p className="mt-[2px] text-[16px] text-white/70 leading-[100%]">
						Invalid address format
					</p>
				)}
			</div>

			{/* Info */}
			<div className="flex flex-col gap-[4px] rounded-[12px] border border-brand-gray bg-brand-dark px-[16px] py-[12px]">
				<div className="flex justify-between">
					<span className="text-[14px] text-white/50 leading-[100%]">Chain</span>
					<span className="flex items-center gap-1.5 text-[14px] text-white leading-[100%]">
						<img
							src={
								sourceKey === 'bsc'
									? '/logos/bnb.svg'
									: sourceKey === 'base'
										? '/logos/base.svg'
										: sourceKey === 'hyperliquid'
											? '/logos/hyperliquid.png'
											: SOURCE_CHAINS[sourceKey as keyof typeof SOURCE_CHAINS].logoURI
							}
							alt={
								sourceKey === 'polygon'
									? 'Polygon'
									: sourceKey === 'bsc'
										? 'BNB Chain'
										: sourceKey === 'base'
											? 'Base'
											: sourceKey === 'hyperliquid'
												? 'Arbitrum'
												: 'Solana'
							}
							className="h-3.5 w-3.5 rounded-full"
						/>
						{sourceKey === 'bsc'
							? 'BNB Chain'
							: sourceKey === 'polygon'
								? 'Polygon'
								: sourceKey === 'base'
									? 'Base'
									: sourceKey === 'hyperliquid'
										? 'Arbitrum'
										: 'Solana'}
					</span>
				</div>
				<div className="flex justify-between">
					<span className="text-[14px] text-white/50 leading-[100%]">Network Fee</span>
					<span className="text-[14px] text-emerald-400 leading-[100%]">
						{sourceKey === 'polygon' || sourceKey === 'bsc' || sourceKey === 'base'
							? 'Sponsored'
							: sourceKey === 'hyperliquid'
								? 'Sponsored'
								: '~0.005 SOL'}
					</span>
				</div>
				{sourceKey === 'hyperliquid' && (
					<div className="flex justify-between">
						<span className="text-[14px] text-white/50 leading-[100%]">HL Bridge Fee</span>
						<span className="text-[14px] text-white leading-[100%]">$1.00</span>
					</div>
				)}
				{sourceKey === 'hyperliquid' && (
					<div className="flex justify-between">
						<span className="text-[14px] text-white/50 leading-[100%]">Est. Time</span>
						<span className="text-[14px] text-white leading-[100%]">~4 min</span>
					</div>
				)}
			</div>

			{/* Error */}
			{error && (
				<div className="flex items-center gap-2 rounded-xl border border-red-500/20 bg-red-500/10 p-3 text-[16px] text-red-400 leading-[100%]">
					<AlertCircle className="h-4 w-4 shrink-0" />
					{error}
				</div>
			)}

			{/* Actions */}
			<div className="flex gap-[12px]">
				<Button
					variant="outline"
					onClick={onBack}
					className="h-12 flex-1 rounded-[12px] border-white/10 text-white hover:bg-white/5"
				>
					Back
				</Button>
				<Button
					onClick={handleSubmit}
					disabled={!canSubmit}
					className="h-12 flex-[2] rounded-[12px] bg-brand-blue font-medium text-white hover:bg-brand-blue/90 disabled:opacity-50"
				>
					Withdraw
				</Button>
			</div>
		</div>
	)
}
